import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import React, { forwardRef, useEffect, useMemo, useState } from "react";
import html2canvas from "html2canvas";
import { Spinner } from "react-bootstrap";
import PreviewTicketPdf from "./a4-prints/index";
import { getPrintingCount } from "Redux/middlwares/suppliers";
import { useAppDispatch } from "Redux/hooks";

const TicketPdfPreview = forwardRef(
  (
    {
      setTicketLoader,
      ticketLoader,
      onHide,
      rebars,
      orderNo,
      selectedRowCount,
      showStandardLogo,
    }: any,
    ref
  ) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation("common");

    const pageSize = 1210;
    const [rebarsRefs, setRebarsRef] = useState();

    const totalPages = useMemo(() => {
      let total = {};
      if (rebarsRefs && (rebarsRefs as any).current)
        total = {
          rebars: (rebarsRefs as any).current.length,
        };

      return total;
    }, [rebarsRefs]);

    useEffect(() => {
      if (
        rebarsRefs &&
        (rebarsRefs as any).current &&
        (rebarsRefs as any).current.length > 0
      ) {
        setTicketLoader(true);
        setTimeout(() => {
          openPrintCanvas();
        }, 100);
      }
    }, [rebarsRefs]);

    // helper to convert html to canvas
const waitForImages = async (
  element: HTMLElement,
  timeoutMs = 15000
): Promise<void> => {
  const images = Array.from(element.querySelectorAll("img"));

  if (images.length === 0) return;

  const imagesReady = Promise.all(
    images.map(
      (image) =>
        new Promise<void>((resolve) => {
          if (image.complete) {
            resolve();
            return;
          }

          const finish = () => {
            image.removeEventListener("load", finish);
            image.removeEventListener("error", finish);
            resolve();
          };

          image.addEventListener("load", finish);
          image.addEventListener("error", finish);
        })
    )
  );

  const timeout = new Promise<void>((resolve) => {
    setTimeout(resolve, timeoutMs);
  });

  await Promise.race([imagesReady.then(() => undefined), timeout]);

  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve());
  });
};

const getHtmlToCanvas = async (element: HTMLElement) => {
  await waitForImages(element, 15000);
  if (Array.from(element.querySelectorAll("img")).some(image => !image.complete || !image.naturalWidth)) {
    throw new Error("Ticket image failed to load");
  }
  if (element.querySelector("[data-ticket-overflow=unresolved]")) {
    throw new Error("Ticket content does not fit without changing the existing design");
  }

  return html2canvas(element, {
    allowTaint: false,
    useCORS: true,
    logging: true,
    backgroundColor: "#ffffff",
    imageTimeout: 15000,
  });
};

    function openPrintCanvas() {
      const canvases = generateCanvases();
      let windowContent: any = "";
      let dataUrl;
      canvases
        .then((values) => {
          values.forEach((canvas: any) => {
            dataUrl = canvas.toDataURL();
            windowContent += "<div style={break-after:page}>";
            windowContent += `<img src=${dataUrl} style="width: 100%; height: auto"/>`;
            windowContent += "</div>";
          });
          setTicketLoader(false);
          onHide();
          let printWin = window.open(
            "https://easybar.co.il/ticket-creator",
            "Easybar.co.il",
            "top=40,left=40,height=700,width=1000,focused=false,resizable=no|0,scrollbars=no,location=no|0"
          );
          // @ts-ignore: Object is possibly 'null'.
          if (printWin) {
            printWin.document.open();
            // @ts-ignore: Object is possibly 'null'.
            printWin.document.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title></title>
                <style>
                  @page {
                    margin: 0;
                    padding: 0;
                    size: A4 Landscape;
                  }
                </style>
              </head>
              <body style="width:100%; height: 100%; margin:0 auto;">
              ${windowContent}
              <script>
                function triggerPrint(event) {
                  window.removeEventListener('load', triggerPrint, false);
                  setTimeout(function() {
                  closeWindow(window.print());
                  }, 100);
                }
                function closeWindow(){
                  window.close();
                }
                window.addEventListener('load', triggerPrint, false);
              </script>
              </body>
            </html>
          `);
            // @ts-ignore: Object is possibly 'null'.
            printWin.document.close();
            dispatch(getPrintingCount());
          } else {
            throw new Error("Unable to open ticket print window");
          }
        })
        .catch((err) => {
        console.error("Failed to generate ticket PDF:", err);
        toast.error(err instanceof Error && err.message === "Ticket content does not fit without changing the existing design"
          ? t("Ticket content does not fit") : t("document action failed", { ns: "newOrder" }));
        setTicketLoader(false);
      });
    }

    async function generateCanvases() {
      const canvases = [];
      if (rebarsRefs) {
        // @ts-ignore: Object is possibly
        for (let i = 0; i < rebarsRefs.current.length; i++) {
          // @ts-ignore: Object is possibly
          const canvas = await getHtmlToCanvas(rebarsRefs.current[i]);
          canvases.push(canvas);
        }
      }
      return canvases;
    }

    // handlers
    const handleRebarsRefs = (refs: any) => {
      setRebarsRef(refs);
    };

    return (
      <>
        <div
          id="PDF-Preview-main"
          style={
            ticketLoader
              ? {
                  pointerEvents: "none",
                }
              : {}
          }
        >
          <PreviewTicketPdf
            pageSize={pageSize}
            loader={ticketLoader}
            rebars={rebars}
            orderNo={orderNo}
            handleRefs={handleRebarsRefs}
            totalPages={totalPages}
            selectedRowCount={selectedRowCount}
            showStandardLogo={showStandardLogo}
          />
        </div>
        {ticketLoader && (
          <div
            className="overloaded-wrapper"
            style={{ position: "absolute", zIndex: "12" }}
          >
            <div className="overloaded-spinner">
              <Spinner animation="border" role="status">
                <span className="visually-hidden"></span>
              </Spinner>
              <p>Generating PDF</p>
            </div>
          </div>
        )}
      </>
    );
  }
);

export default TicketPdfPreview;
